import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import TopNav from "../../components/TopNav/TopNav";
import SideBar from "../../components/SideBar/SideBar";
import Button from "../../components/Buttons/Button";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import Skeleton from "../../components/Skeleton/Skeleton";
import './CancellationRequests.scss';

const ListCancellationRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  // Optional denial reason captured in the confirm dialog.
  const [denyNotes, setDenyNotes] = useState('');

  const fetchData = async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await api.get("/event-cancellation-requests", {
        params: { status: 'pending', page: 1, records: 100 },
      });
      setRequests(response.data.data || []);
    } catch (error) {
      console.error("Error fetching cancellation requests:", error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReview = async () => {
    if (!confirmAction) return;
    try {
      const body = { status: confirmAction.type };
      if (confirmAction.type === 'rejected' && denyNotes.trim()) {
        body.admin_notes = denyNotes.trim();
      }
      const response = await api.post(
        `/event-cancellation-requests/${confirmAction.id}/review`,
        body
      );

      const data = response?.data || {};
      // Server returns a human-readable message, e.g.
      // "Refunded N customers, notified M". Fall back to a sensible default.
      toast.success(data.message || (confirmAction.type === 'approved' ? 'Cancellation approved' : 'Cancellation denied'));

      // Surface any refunds that failed so the admin can follow up manually.
      const failures = data.refundFailures;
      if (Array.isArray(failures) && failures.length > 0) {
        toast.warn(`${failures.length} refund${failures.length === 1 ? '' : 's'} failed and need manual follow-up`);
      }

      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to process cancellation request';
      toast.error(msg);
    } finally {
      setConfirmAction(null);
      setDenyNotes('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#2ECC71';
      case 'rejected': return '#E74C3C';
      case 'processed': return '#0A3FD6';
      default: return '#F1C40F';
    }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="cancellations">
            <ConfirmModal
              isOpen={!!confirmAction}
              title={confirmAction?.type === 'approved' ? 'Approve Cancellation' : 'Deny Cancellation'}
              message={
                confirmAction?.type === 'approved'
                  ? `Approve the cancellation of "${confirmAction?.event}"? This will deactivate the event and refund + notify all paying customers.`
                  : `Deny the cancellation request for "${confirmAction?.event}"?`
              }
              confirmLabel={confirmAction?.type === 'approved' ? 'Approve' : 'Deny'}
              danger={confirmAction?.type === 'rejected'}
              onConfirm={handleReview}
              onCancel={() => { setConfirmAction(null); setDenyNotes(''); }}
            >
              {confirmAction?.type === 'rejected' && (
                <textarea
                  className="cancellations__deny-reason"
                  placeholder="Reason for denial (optional)"
                  value={denyNotes}
                  onChange={(e) => setDenyNotes(e.target.value)}
                  rows={3}
                />
              )}
            </ConfirmModal>

            <h1 className="cancellations__title">Cancellation Requests</h1>

            {isLoading ? (
              <div className="cancellations__loading">
                <Skeleton type="row" />
                <Skeleton type="row" />
                <Skeleton type="row" />
                <Skeleton type="row" />
              </div>
            ) : hasError ? (
              <div className="cancellations__empty">
                <span className="iconify" data-icon="solar:danger-triangle-outline" style={{ fontSize: 48, color: 'rgba(231,76,60,0.4)' }}></span>
                <p>Couldn't load cancellation requests</p>
                <button className="cancellations__view-btn" onClick={fetchData}>Retry</button>
              </div>
            ) : requests.length === 0 ? (
              <div className="cancellations__empty">
                <span className="iconify" data-icon="solar:calendar-mark-outline" style={{ fontSize: 48, color: 'rgba(255,255,255,0.12)' }}></span>
                <p>No pending cancellation requests</p>
              </div>
            ) : (
              <div className="cancellations__list">
                {requests.map((request) => {
                  const isExpanded = expandedId === request._id;
                  const requesterName = `${request.requested_by?.firstName || ''} ${request.requested_by?.lastName || ''}`.trim();
                  const eventName = request.event?.name || 'Untitled event';

                  return (
                    <div key={request._id} className="cancellations__card">
                      <div className="cancellations__row" onClick={() => setExpandedId(isExpanded ? null : request._id)}>
                        <div className="cancellations__event">
                          <div className="cancellations__name">{eventName}</div>
                          <div className="cancellations__meta">{requesterName || request.requested_by?.email || 'Unknown requester'}</div>
                        </div>
                        <div className="cancellations__reason" title={request.reason || ''}>
                          {request.reason || 'No reason provided'}
                        </div>
                        <span className="cancellations__status" style={{ color: getStatusColor(request.status), background: getStatusColor(request.status) + '18' }}>
                          {request.status}
                        </span>
                        <div className="cancellations__date">{request.event?.date ? new Date(request.event.date).toLocaleDateString() : '—'}</div>
                        <div className="cancellations__expand" style={{ transform: isExpanded ? 'rotate(180deg)' : '' }}>
                          <span className="iconify" data-icon="solar:alt-arrow-down-outline" style={{ fontSize: 16 }}></span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="cancellations__details">
                          <div className="cancellations__detail-grid">
                            <div>
                              <div className="cancellations__detail-label">Reason</div>
                              <div className="cancellations__detail-value">{request.reason || 'No reason provided'}</div>
                            </div>
                            <div>
                              <div className="cancellations__detail-label">Requested by</div>
                              <div className="cancellations__detail-value">{requesterName || '—'}</div>
                            </div>
                            <div>
                              <div className="cancellations__detail-label">Requester Email</div>
                              <div className="cancellations__detail-value">{request.requested_by?.email || '—'}</div>
                            </div>
                            <div>
                              <div className="cancellations__detail-label">Event Date</div>
                              <div className="cancellations__detail-value">{request.event?.date ? new Date(request.event.date).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—'}</div>
                            </div>
                            <div>
                              <div className="cancellations__detail-label">Event Status</div>
                              <div className="cancellations__detail-value" style={{ textTransform: 'capitalize' }}>{request.event?.status || '—'}</div>
                            </div>
                            <div>
                              <div className="cancellations__detail-label">Requested</div>
                              <div className="cancellations__detail-value">{request.created_at ? new Date(request.created_at).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—'}</div>
                            </div>
                            {request.refund_summary && (
                              <div>
                                <div className="cancellations__detail-label">Refund Summary</div>
                                <div className="cancellations__detail-value">{request.refund_summary}</div>
                              </div>
                            )}
                            {typeof request.notified_count === 'number' && (
                              <div>
                                <div className="cancellations__detail-label">Customers to Notify</div>
                                <div className="cancellations__detail-value">{request.notified_count}</div>
                              </div>
                            )}
                          </div>

                          <div className="cancellations__actions">
                            {request.requested_by?._id && (
                              <button className="cancellations__view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/users/${request.requested_by._id}`); }}>
                                View User
                              </button>
                            )}
                            {request.status === 'pending' && (
                              <>
                                <Button primaryBtn onClick={(e) => { e.stopPropagation(); setConfirmAction({ id: request._id, type: 'approved', event: eventName }); }}>
                                  Approve
                                </Button>
                                <button className="cancellations__reject-btn" onClick={(e) => { e.stopPropagation(); setDenyNotes(''); setConfirmAction({ id: request._id, type: 'rejected', event: eventName }); }}>
                                  Deny
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default ListCancellationRequests;
